<template>
  <TheMenuLayout>
    <TheBreadcrumbs class="pl-0" :items="breadcrumbItems" />
    <BaseSectionHeader>Votre score</BaseSectionHeader>
    <v-row>
      <v-col>
        <BaseNumberCard
          class="mb-4"
          :color="`${scoreColor} lighten-3`"
          :title="scoreTitle"
        >
          <span>
            {{ typeof(studentTest.score_ratio_over_20) === 'number' ? studentTest.score_ratio_over_20.toFixed(2) : '' }}/20
            <span v-if="scoreDifference !== null">({{ scoreDifference }})</span>
          </span>
        </BaseNumberCard>
      </v-col>
      <v-col>
        <BaseNumberCard class="mb-4" color="grey lighten-3" title="Durée">
          <span>
            {{ (studentTest.duration / 60).toFixed() }} min
            <span v-if="durationDifference !== null"> ({{ durationDifference }} min) </span>
          </span>
        </BaseNumberCard>
      </v-col>
    </v-row>
    <BaseButton @click="restartTest" color="primary" class="mb-4">
      <div v-if="this.isConcoursBlanc">Refaire le concours blanc</div>
      <div v-else>Refaire le QCM</div>
    </BaseButton>

    <BaseSectionHeader class="mb-4">Correction : {{ currentTest.name }}</BaseSectionHeader>
    <BaseButton @click="correctionDisplayed = !correctionDisplayed" class="mb-4">
      <template v-if="correctionDisplayed">
        Masquer la correction
      </template>
      <template v-else>
        Afficher la correction
      </template>
    </BaseButton>
    <transition name="fade">
      <BaseCardList v-show="correctionDisplayed" :items="items" />
    </transition>
  </TheMenuLayout>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
  every, includes, find, isNil, sortBy,
} from 'lodash';
import TheMenuLayout from '@/components/TheMenuLayout.vue';
import BaseCardList from '@/components/BaseCardList.vue';
import BaseSectionHeader from '@/components/BaseSectionHeader.vue';
import BaseNumberCard from '@/components/BaseNumberCard.vue';
import TheBreadcrumbs from '@/components/TheBreadcrumbs.vue';
import BaseButton from '@/components/BaseButton.vue';

export default {
  name: 'TestResult',
  components: {
    TheMenuLayout,
    BaseCardList,
    BaseSectionHeader,
    BaseNumberCard,
    TheBreadcrumbs,
    BaseButton,
  },
  created() {
    this.fetchCurrentTest(this.testId);
    this.fetchTestCorrections(this.testId);
  },
  mounted() {
    this.finishPendingStudentTest(this.testId);
  },
  data() {
    return {
      correctionDisplayed: false,
    };
  },
  computed: {
    ...mapState(['currentTest', 'currentTestCorrections', 'studentTest']),
    items() {
      return this.currentTest.questions.map((question) => ({
        title: question.title,
        key: question.key,
        text: question.content.body,
        status: this.isCorrect(question),
        score: this.findScore(question),
        bottomTitle: this.findStats(question),
        bottomSubTitle: question.written_answer ? null : 'Correction',
        subItems: sortBy(question.possible_answers, 'text').map((answer) => ({
          title: answer.text,
          success: this.isAnswerCorrect(answer),
        })),
      }));
    },
    testId() {
      return this.$route.params.id;
    },
    breadcrumbItems() {
      return [
        {
          text: this.isConcoursBlanc ? 'Concours blancs' : 'QCMs',
          disabled: false,
          href: this.isConcoursBlanc ? '/concours_blancs' : '/qcm',
        },
        {
          text: this.currentTest.name,
          disabled: true,
          href: `/qcm/${this.currentTest.id}`,
        },
        {
          text: 'Résultats',
          disabled: true,
          href: `/qcm/${this.currentTest.id}/resultat`,
        },
      ];
    },
    scoreDifference() {
      if (!isNil(this.studentTest.previous_score)) {
        let diff = this.studentTest.score_ratio_over_20 - this.studentTest.previous_score;
        if (diff > 0) {
          diff = `+${diff}`;
        }
        return diff;
      }
      return null;
    },
    durationDifference() {
      if (!isNil(this.studentTest.previous_duration)) {
        let difference = this.studentTest.duration - this.studentTest.previous_duration;
        difference = parseInt((difference / 60).toFixed(), 10);
        if (difference > 0) {
          difference = `+${difference.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        } else if (difference < 0) {
          difference = difference.toLocaleString(undefined, { maximumFractionDigits: 2 });
        }
        return difference;
      }
      return null;
    },
    scoreTitle() {
      if (this.studentTest.is_score_temporary) {
        return 'Score temporaire';
      }
      return 'Score';
    },
    scoreColor() {
      if (this.studentTest.is_score_temporary) {
        return 'warning';
      }
      return this.studentTest.score_ratio_over_20 >= 10 ? 'teal' : 'red';
    },
    isConcoursBlanc() {
      return this.currentTest.white_test === true;
    },
  },
  methods: {
    ...mapActions([
      'fetchCurrentTest',
      'fetchTestCorrections',
      'finishPendingStudentTest',
      'newStudentTest',
    ]),
    isCorrect(question) {
      const studentTestWrittenAnswer = this.studentTest.student_test_written_answers.find((answer) => answer.question_id === question.id);
      if (studentTestWrittenAnswer && studentTestWrittenAnswer.score === 0) {
        return 'error';
      } if (studentTestWrittenAnswer && !studentTestWrittenAnswer.score) {
        return 'temporary';
      } if (studentTestWrittenAnswer && studentTestWrittenAnswer.score === question.point) {
        return 'success';
      } if (studentTestWrittenAnswer) {
        return 'partial';
      }

      const answerIds = this.studentTest.answer_ids;
      const answerStatus = every(
        question.possible_answers,
        (possibleAnswer) => (this.isAnswerCorrect(possibleAnswer) && includes(answerIds, possibleAnswer.id))
          || (!this.isAnswerCorrect(possibleAnswer) && !includes(answerIds, possibleAnswer.id)),
      );
      if (answerStatus) {
        return 'success';
      }
      return 'error';
    },
    findScore(question) {
      const studentTestWrittenAnswer = this.studentTest.student_test_written_answers.find((answer) => answer.question_id === question.id);
      if (!studentTestWrittenAnswer) return null;
      return `${studentTestWrittenAnswer.score}/${question.point}`;
    },
    findStats(question) {
      const { statistics } = find(this.currentTest.questions, { id: question.id });
      if (question.written_answer) {
        return `Note moyenne : ${statistics[0] || ''}/${question.point}, nombre de participants corrigés : ${statistics[1]}`;
      }
      return `Bonnes réponses : ${statistics[0]}%, nombre de participants : ${statistics[1]}`;
    },
    isAnswerCorrect(answer) {
      return !!find(this.currentTestCorrections, { id: answer.id, correct: true });
    },
    restartTest() {
      this.newStudentTest().then(() => {
        if (this.isConcoursBlanc) {
          this.$router.push(`/concours_blancs/${this.currentTest.id}`);
        } else {
          this.$router.push(`/qcm/${this.currentTest.id}`);
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
